/**
 * Rinsey storefront enhancements. Loaded on every page (deferred).
 * Everything here layers on top of Dawn's cart drawer and degrades to Dawn's
 * default behaviour if anything is missing.
 */

/* 1. "Added to cart" heading.
 *    Dawn re-renders the drawer after an add and opens it. An add response carries the line
 *    item (it has an `id`); a quantity change or removal response does not. Only adds get the
 *    confirmation heading, so it never shows on unrelated updates. */
customElements.whenDefined('cart-drawer').then(() => {
  const CartDrawerElement = customElements.get('cart-drawer');
  if (!CartDrawerElement || CartDrawerElement.prototype.__rinseyPatched) return;

  const renderContents = CartDrawerElement.prototype.renderContents;

  CartDrawerElement.prototype.renderContents = function (parsedState) {
    renderContents.call(this, parsedState);

    const isAdd = Boolean(parsedState && parsedState.id && parsedState.quantity);
    const heading = this.querySelector('.drawer__heading');
    if (isAdd && heading && heading.dataset.addedHeading) {
      heading.textContent = heading.dataset.addedHeading;
    }
  };

  CartDrawerElement.prototype.__rinseyPatched = true;
});

/* 2. One-click add for the cartridge cross-sell (cart drawer) and the sticky Add to Cart bar.
 *    Mirrors what Dawn's own product form does after an add: clear the drawer's empty state, remember
 *    the trigger for focus return, then hand the response to the drawer so it re-renders and opens.
 *    If the store uses the cart page instead of a drawer, it goes to the cart. */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-rinsey-add-variant]');
  if (!button || button.disabled) return;

  const drawer = document.querySelector('cart-drawer');
  if (!window.routes) return;

  const oldError = button.parentElement && button.parentElement.querySelector('.rinsey-add-error');
  if (oldError) oldError.remove();

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: Number(button.dataset.rinseyAddVariant),
        quantity: 1,
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    });

    const state = await response.json();
    if (!response.ok || state.status) throw new Error(state.description || state.message || 'Could not add item');

    if (drawer) {
      drawer.classList.remove('is-empty');
      if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(button);
      drawer.renderContents(state);
      button.disabled = false;
      button.removeAttribute('aria-busy');
    } else {
      window.location.assign(window.routes.cart_url);
    }
  } catch (error) {
    console.error('[rinsey] add to cart failed', error);
    button.disabled = false;
    button.removeAttribute('aria-busy');

    // Tell the shopper, not just the console.
    const message = document.createElement('p');
    message.className = 'rinsey-add-error';
    message.setAttribute('role', 'alert');
    message.textContent = "We couldn't add that. Please try again.";
    button.insertAdjacentElement('afterend', message);
  }
});

/* 3. Sticky Add to Cart bar: show it only once the main buy button has scrolled up out of view. */
document.addEventListener('DOMContentLoaded', () => {
  const bar = document.querySelector('[data-rinsey-sticky]');
  const form = document.querySelector('product-form');
  if (!bar || !form || !('IntersectionObserver' in window)) return;

  new IntersectionObserver(([entry]) => {
    const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    bar.classList.toggle('is-visible', show);
    bar.toggleAttribute('inert', !show);
    bar.setAttribute('aria-hidden', String(!show));
  }).observe(form);
});

/* 4. Homepage postcode box. Hand the postcode to the checker page through sessionStorage rather than the
 *    URL, so it doesn't end up in analytics or browser history. (Without JS the form still works as a
 *    plain GET to the checker, which reads ?postcode= as a fallback.) */
document.addEventListener('submit', (event) => {
  const form = event.target.closest('.rinsey-hero__check');
  if (!form) return;

  const value = (form.querySelector('input[name="postcode"]') || {}).value;
  if (!value) return;

  try {
    window.sessionStorage.setItem('rinseyPostcode', value);
  } catch (error) {
    return; // storage blocked: let the plain GET go through
  }

  event.preventDefault();
  window.location.assign(form.getAttribute('action'));
});

/* 5. Click-to-reveal email address. The address is stored reversed in the markup, so it never appears
 *    in the HTML as a plain address or mailto link for scrapers to harvest. */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-rinsey-email-button]');
  if (!button) return;

  const holder = button.closest('[data-rinsey-email]');
  const address = holder.dataset.rinseyEmail.split('').reverse().join('');

  const link = document.createElement('a');
  link.href = `mailto:${address}`;
  link.textContent = address;
  link.className = 'rinsey-textlink';
  holder.replaceChildren(link);
  link.focus();
});

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-rinsey-email-button]').forEach((button) => {
    button.hidden = false;
  });
});

/* 6. Motion: scroll reveal and number count-up.
 *    CSS (rinsey.css) hides the targets below only while <html> has .rinsey-anim, which the head script adds
 *    for visitors who have not asked for reduced motion. Here each target gets .is-in as it enters the
 *    viewport, with a small stagger between siblings. The selector list must match the one in rinsey.css. */
(function () {
  const root = document.documentElement;
  if (!root.classList.contains('rinsey-anim')) return;

  const TARGETS = [
    '.rinsey-eyebrow', '.rinsey-h2', '.rinsey-lede', '.rinsey-reframe__before', '.rinsey-reframe__after',
    '.rinsey-fact', '.rinsey-facts__footnote', '.rinsey-steps li', '.rinsey-compare__scroll',
    '.rinsey-card', '.rinsey-review', '.rinsey-faq__title', '.rinsey-faq__group', '.rinsey-faq__item',
    '.rinsey-banner__title', '.rinsey-cta-row', '.rinsey-detail__row', '.rinsey-editorial__body',
    '.rinsey-editorial__closer', '.rinsey-contact__grid > *', '.rinsey-footer__grid > *',
    '.rinsey-checker__map', '.rinsey-checker__title', '.rinsey-checker__form',
  ].join(',');

  const countUp = (el) => {
    const match = String(el.dataset.rinseyCount || '').match(/^(\d+)(.*)$/);
    if (!match) return;
    const end = parseInt(match[1], 10);
    const suffix = match[2];
    const duration = 1400;
    const start = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out
      el.textContent = Math.round(end * eased) + suffix;
      if (t < 1) requestAnimationFrame(tick);
    };
    el.textContent = '0' + suffix;
    requestAnimationFrame(tick);
  };

  const start = () => {
    const seen = new Map();
    const items = [...document.querySelectorAll(TARGETS)].filter((el) => !el.closest('.rinsey-hero'));

    // Stagger siblings that share a parent, capped so long lists don't drag.
    items.forEach((el) => {
      const n = seen.get(el.parentElement) || 0;
      el.style.setProperty('--rinsey-i', Math.min(n, 5));
      seen.set(el.parentElement, n + 1);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          el.classList.add('is-in');
          const number = el.querySelector && el.querySelector('[data-rinsey-count]');
          if (number) countUp(number);
          observer.unobserve(el);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );

    items.forEach((el) => observer.observe(el));
    root.classList.add('rinsey-ready');
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();